import AppManager from './_app.manager';
import { Provider } from 'react-redux';
import { useRouter } from 'next/router';
import axios from 'axios';
import { API_HEADERS } from 'Utils/headers';
import { store } from '../store/store';
import BootstrapLoading from '../components/shared/BootstrapLoading/BootstrapLoading';
import { useEffect, useState } from 'react';
import * as gtag from 'Lib/gtag';
import getI18n from 'Lib/i18n';
import { I18nextProvider } from 'react-i18next';
import getConfig from 'next/config';
import Script from 'next/script';
import { Poppins, Manrope } from 'next/font/google';
import SocketManager from 'Components/services/SocketManager';
import { getTargetLocale } from 'Utils/locale';
import { DEPLOY_ENVIRONMENT } from 'Types/globals';

const { publicRuntimeConfig } = getConfig();

const poppins = Poppins({
  subsets: ['latin', 'latin-ext'],
  weight: ['400', '500', '600', '700', '800'],
});
const manrope = Manrope({
  subsets: ['cyrillic', 'cyrillic-ext'],
  weight: ['400', '500', '600', '700', '800'],
});

declare global {
  interface Window {
    bt: any;
    BTRenderer?: () => void;
    sportsUnloaded: boolean;
    widgetInstance: any;
    dataLayer: Record<string, any>[];
    registerServiceWorkers: any;
  }
}

interface IServiceWorkerConfig {
  path: string;
  canInstall: boolean;
  data: {
    redirectTxt?: string;
  };
}

const DefaultApp = (props: any) => {
  const router = useRouter();
  const [loading, SetLoading] = useState(true);
  let forcedLocale = router.locale;

  const [i18nInstance] = useState(() => getI18n(props.i18n, forcedLocale));

  if (typeof window !== 'undefined') {
    if (window.location.pathname !== router?.pathname && router?.locale === 'en') {
      props.countryData.locale = 'en';
      forcedLocale = 'en';
    }
  }

  const getClientJs = async () => {
    const { ClientJS } = await import('clientjs');

    const client = new ClientJS();

    return client;
  };

  useEffect(() => {
    document.fonts.ready.then(async () => {
      const client = await getClientJs();

      const ua = client?.getUserAgent();
      const canvasPrint = client?.getCanvasPrint?.();
      const CPU = client.getCPU(); // Get CPU Architecture
      const fgp = client?.getCustomFingerprint?.(ua, canvasPrint, CPU);

      localStorage?.setItem?.('fgp', fgp?.toString());
      localStorage?.setItem?.('g_fgp', fgp?.toString());

      gtag.setVariables({
        locale: router.locale ?? 'en',
        userAgent: ua,
        cpu: CPU,
        customFingerprint: fgp,
      });
      SetLoading(false);
    });
  }, []);

  return (
    <>
      <style jsx global>{`
        :root {
          --default-font-family: ${poppins.style.fontFamily};
          --Poppins: ${poppins.style.fontFamily};
          --Manrope: ${manrope.style.fontFamily};
        }
      `}</style>
      <Script
        src={`/sw.js?v=${publicRuntimeConfig?.version}`}
        async
        onLoad={e => {
          if (typeof window !== 'undefined') {
            const swConfig: Array<IServiceWorkerConfig> = [
              {
                path: '/redirector.js',
                canInstall: props.redirectTxt,
                data: {
                  redirectTxt: props.redirectTxt,
                },
              },
              {
                path: '/tokenManager.js',
                canInstall: true,
                data: {},
              },
            ];

            window?.registerServiceWorkers?.({
              buildVersion: publicRuntimeConfig?.version,
              config: swConfig,
            });
          }
        }}
      />
      <BootstrapLoading isLoading={loading} />
      <Provider store={store}>
        <I18nextProvider i18n={i18nInstance}>
          <AppManager {...props} forcedLocale={forcedLocale} />
        </I18nextProvider>
        <SocketManager />
      </Provider>
    </>
  );
};

function makeid(length) {
  let result = '';
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

// allows any component to use intial props
DefaultApp.getInitialProps = async ({ Component, ctx }) => {
  let pageProps = {};

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  let i18n = {
    loading: true,
  };
  let integrationKeys = {
    loading: true,
  };
  const { req, res } = ctx;
  const isActualRoute = ctx?.req?.url?.indexOf('_next/data') === -1 && ctx?.req?.url?.indexOf('/images/') === -1;

  const cookies = res?.req?.cookies ?? req?.cookies ?? {};

  let countryData: any = {
    isGuest: cookies?._token === undefined,
  };

  const cfCountry = req?.headers['cf-ipcountry'];
  const cfIp = req?.headers['cf-connecting-ip'];
  const forwardedIp = req?.headers['x-forwarded-for'];
  const isMirror = req?.headers['sw-alternate-route'];

  let { uuid = null, countryOverride, preferredLocale, defaultLocale } = cookies;

  const isProduction = process?.env?.DEPLOY_ENV === DEPLOY_ENVIRONMENT.production;

  const country = isProduction ? cfCountry ?? countryOverride : countryOverride ?? cfCountry ?? 'SE';
  const ip = cfIp ?? forwardedIp ?? process.env.CUSTOM_IP ?? '8.8.8.8';

  const targetLocale = getTargetLocale({ country, defaultLocale, preferredLocale });

  if (isActualRoute && typeof window === 'undefined') {
    try {
      const newHeaders = [];

      if (!uuid || uuid?.length < 32 || uuid === 'undefined') {
        uuid = makeid(32);
        newHeaders?.push(`uuid=${uuid}; path=/; Max-Age=${60 * 60 * 24 * 7 * 4};`);
      }

      newHeaders?.push(`ip=${ip}; path=/;`);
      newHeaders?.push(`country=${country}; path=/;`);

      let translations: any = {};
      try {
        if (isActualRoute && typeof window === 'undefined') {
          translations = await axios.get(`${process.env.I18N_URL_NEW}/i18n-${targetLocale}.json`);
          // console.log(translations);

          i18n = {
            ...translations.data,
            loading: false,
          };
        }
      } catch (e) {
        throw new Error('Translations error');
      }

      let predefinedDepositsPromise: Promise<any> | undefined;
      // Skip predefined deposits for Finland and Estonia
      if (['FI', 'FIN', 'EE', 'EST'].includes(country)) {
        predefinedDepositsPromise = axios.get(process.env.PREDEFINED_DEPOSITS_URL);
      }

      const bootstrapPromise: any = axios.get(`${process.env.BACKAPI_URL}/bootstrap/v2`, {
        auth: {
          username: process.env.BACK_API_AUTH_LOGIN,
          password: process.env.BACK_API_AUTH_PASS,
        },
        headers: {
          [API_HEADERS.UUID]: uuid,
          [API_HEADERS.COUNTRY]: country,
          [API_HEADERS.IP]: ip,
          [API_HEADERS.USER_AGENT]: req.headers?.['user-agent'],
        },
      });

      const isSportsbookEnabled = !!parseInt(process.env.SPORTSBOOK_ENABLED);

      const [bootstrap, deposits] = await Promise.all([bootstrapPromise, predefinedDepositsPromise]);

      newHeaders?.push(`locale=${targetLocale}; path=/; Max-Age=${60 * 60 * 24 * 7 * 4};`);

      res?.setHeader?.('Set-Cookie', newHeaders);

      const geo = { ...bootstrap.data.country };

      delete bootstrap.data.country;

      countryData = {
        ...geo,
        ...bootstrap.data,
        sportsbook: isSportsbookEnabled ? bootstrap?.data?.sportsbook : null,
        ip: ip,
        isGuest: cookies?._token === undefined,
        isMirror: isMirror,
        locale: targetLocale,
        deposits: deposits?.data ?? [],
        redirectTxt: process.env.REDIRECT_TXT,
      };
    } catch (e) {
      countryData = {
        deny: true,
        code: 512,
        locale: 'en',
        isGuest: true,
      };
    }
  }
  return {
    pageProps,
    i18n,
    countryData,
    integrationKeys,
    umami_id: process.env.UMAMI_ID,
    redirectTxt: process.env.REDIRECT_TXT,
    seonSourceUrl: process.env.SEON_SOURCE_URL ?? 'https://cdn.seondf.com/js/v6/agent.umd.js',
  };
};

export default DefaultApp;
